import { useRef, useEffect, useMemo, useState } from "react";

import { Drawer, serverResponseFormat } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Badge } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { CollapsiblePromptsList } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/CollapsiblePromptsList";
import { PromptResponseSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/PromptResponseSection";
import type { ITabItem } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/SidePanelSwitcher";
import { SidePanelSwitcher } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/SidePanelSwitcher";
import { useUrlSidePanel } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/hooks/useAnalyzedUrls";
import type { IUrlSidePanelItem } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/types";
import {
    MentionStatus,
    type IAnalyzedURL,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/url-table/types";
import { getBaseWidgetRequestParams } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { PromptCard } from "app/pages/digital-marketing/generative-ai/shared/PromptCard";
import { SidePanelEmptyState } from "app/pages/digital-marketing/generative-ai/shared/SidePanelEmptyState";

import { useSourcesSidePanelDatePicker } from "./hooks/useSourcesSidePanelDatePicker";

const MAX_SIDE_PANEL_MONTHS = 3;

interface IPromptsSidePanel extends Pick<ILLMReportWidget, "campaign" | "campaignConfig" | "reportDuration"> {
    isOpen: boolean;
    urlData: IAnalyzedURL;
    onClose: () => void;
    topics?: string[];
}
export const SourcesReportTableSidePanel = ({
    isOpen,
    urlData,
    onClose,
    campaign,
    campaignConfig,
    reportDuration,
    topics,
}: IPromptsSidePanel) => {
    const [selectedFilter, setSelectedFilter] = useState<MentionStatus>(MentionStatus.Mentioned);
    const listContainerRef = useRef<HTMLDivElement>(null);
    const enabled = isOpen && !!urlData?.rawUrl;

    const cappedFrom = useMemo(() => {
        const maxRangeStartDate = reportDuration.to.subtract(MAX_SIDE_PANEL_MONTHS, "month");
        return maxRangeStartDate.isAfter(reportDuration.from) ? maxRangeStartDate : reportDuration.from;
    }, [reportDuration.from, reportDuration.to]);

    const { data, isLoading, isError } = useUrlSidePanel(
        {
            ...getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
            startDate: cappedFrom.format(serverResponseFormat),
            endDate: reportDuration.to.format(serverResponseFormat),
            url: urlData?.rawUrl || "",
            ...(topics ? { topics } : {}),
        },
        { enabled },
    );

    const allData: IUrlSidePanelItem[] = data ?? [];

    const { currentDateData, datePicker } = useSourcesSidePanelDatePicker({
        data: allData,
    });

    const filteredPrompts = useMemo(
        () =>
            currentDateData
                .filter((p) => (selectedFilter === MentionStatus.Mentioned ? p.mentioned : !p.mentioned))
                .sort((a, b) => a.prompt.localeCompare(b.prompt)),
        [currentDateData, selectedFilter],
    );

    const mentionCounts = useMemo(
        () => ({
            [MentionStatus.Mentioned]: currentDateData.filter((p) => p.mentioned).length,
            [MentionStatus.NotMentioned]: currentDateData.filter((p) => !p.mentioned).length,
        }),
        [currentDateData],
    );

    const switcherItems = useMemo<ITabItem[]>(
        () => [
            {
                id: MentionStatus.Mentioned,
                label: translate("llm.sources.prompt.side.panel.filter.mentioned", {
                    count: mentionCounts[MentionStatus.Mentioned],
                }),
            },
            {
                id: MentionStatus.NotMentioned,
                label: translate("llm.sources.prompt.side.panel.filter.not.mentioned", {
                    count: mentionCounts[MentionStatus.NotMentioned],
                }),
            },
        ],
        [mentionCounts],
    );

    const isEmpty = !isError && currentDateData.length === 0;

    useEffect(() => {
        if (currentDateData.length === 0) return;
        const hasMentioned = currentDateData.some((p) => p.mentioned);
        const hasNotMentioned = currentDateData.some((p) => !p.mentioned);
        if (hasMentioned) {
            setSelectedFilter(MentionStatus.Mentioned);
        } else if (hasNotMentioned) {
            setSelectedFilter(MentionStatus.NotMentioned);
        }
    }, [currentDateData]);

    useEffect(() => {
        listContainerRef?.current?.scrollTo({ top: 0 });
    }, [selectedFilter, currentDateData]);

    return (
        <Drawer
            isOpen={isOpen}
            onOpenChange={(state) => !state && onClose()}
            isLoading={isLoading}
            size="large"
            shouldCloseOnInteractOutside={(el) => !el.closest(".DropdownButton-popup-container")}
        >
            <Drawer.Header title={translate("llm.sources.prompt.side.panel.title")} />
            <Drawer.SubHeader>
                <Flex
                    flexDirection="column"
                    width="100%"
                >
                    <SidePanelSwitcher
                        key={selectedFilter}
                        items={switcherItems}
                        selectedValue={selectedFilter}
                        onItemClick={(value) => setSelectedFilter(value as MentionStatus)}
                    />
                    <Box
                        alignItems="center"
                        margin="0 0 8px 8px"
                        borderTop={`1px solid ${colorsPalettes.carbon[100]}`}
                        padding="8px 0 0 0"
                        data-automation="sources-side-panel-date-picker"
                    >
                        {datePicker}
                    </Box>
                </Flex>
            </Drawer.SubHeader>
            <Drawer.Content>
                <Flex
                    flexDirection="column"
                    gap="16px"
                    flex="1"
                    minHeight="0"
                    overflow="hidden"
                >
                    {isEmpty ? (
                        <SidePanelEmptyState
                            title={translate("llm.sources.prompt.side.panel.no.data.title")}
                            subtitle={translate("llm.sources.prompt.side.panel.no.data.subtitle")}
                        />
                    ) : filteredPrompts.length > 0 ? (
                        <CollapsiblePromptsList
                            title={translate("llm.sources.prompt.side.panel.subtitle", {
                                count: filteredPrompts.length,
                            })}
                            listRef={listContainerRef}
                        >
                            {filteredPrompts.map((p, index) => (
                                <PromptCard
                                    key={`${p.prompt}-${index}`}
                                    text={p.prompt}
                                    dataAutomation="sources-side-panel-prompt-card"
                                    textFooterElements={[
                                        <Badge
                                            key="mention"
                                            bgColor={p.mentioned ? colorsPalettes.green[100] : colorsPalettes.red[100]}
                                            color={p.mentioned ? colorsPalettes.green[500] : colorsPalettes.red[500]}
                                            type="custom"
                                        >
                                            {p.mentioned
                                                ? translate("llm.sources.prompt.side.panel.mentioned")
                                                : translate("llm.sources.prompt.side.panel.not.mentioned")}
                                        </Badge>,
                                        <Badge
                                            key="topic"
                                            data-automation="sources-side-panel-prompt-topic"
                                            bgColor={colorsPalettes.carbon[25]}
                                            color={colorsPalettes.carbon[400]}
                                            type="custom"
                                        >
                                            {p.topics?.[0]}
                                        </Badge>,
                                    ]}
                                >
                                    <PromptResponseSection
                                        campaign={campaign}
                                        responseText={p.response}
                                    />
                                </PromptCard>
                            ))}
                        </CollapsiblePromptsList>
                    ) : (
                        <SidePanelEmptyState
                            title={translate("llm.sources.prompt.side.panel.no.data.title")}
                            subtitle={translate("llm.sources.prompt.side.panel.no.data.subtitle")}
                        />
                    )}
                </Flex>
            </Drawer.Content>
        </Drawer>
    );
};
